import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue, useSetAtom } from 'jotai';

import { Card } from '@growtherapy/sprout-ui';

import {
  NudgeObject,
  TreatmentPlanStatus,
  TreatmentPlansOverviewConnected,
  TreatmentPlansOverviewConnectedProps,
  useShouldPromptForCSSRS,
} from '@grow-therapy-team/seedling-components';

import { EventTypeEnum } from '../../../segment/types';
import { showAnytimeCSSRSGate } from '../../../statsig/gates';
import {
  ProviderDrawerState,
  drawerStateAtom,
  isInSessionWithSelectedClientAtom,
  patientHasCompletedMeasureResponsesFamily,
  patientShortIdToMeasuresFetchFailedAtom,
  patientShortIdWithFetchedMeasuresAtom,
  selectedClientInfoAtom,
  selectedClientMeasuresData,
  selectedPatientId,
  treatmentPlanContentStatus,
} from '../../state';
import { useGetFormsByPatientShortId } from '../../useGetFormsByPatientShortId';
import {
  ClientInformationFormsAndQuestionnaires,
  type ClientInformationFormsAndQuestionnairesProps,
} from '../ClientInformationFormsAndQuestionnaires';
import { ClientInformationProgressWrapper as ClientInformationProgress } from '../ClientInformationProgressWrapper';
import { ClinicalConsiderationsSectionWrapper as ClinicalConsiderationsSection } from '../ClinicalConsiderationsSection';
import { completedFormsForSelectedClientAtom, hasNewFormsAtomFamily } from '../state';
import { CombinedFormDataEntriesByFormTitle } from '../types';
import { useSendClientInfoTabViewTrackingEvent } from '../useSendClientInfoTabViewTrackingEvent';

type FormsCssrsPromptProps = Pick<
  ClientInformationFormsAndQuestionnairesProps,
  'cssrsPromptLoading' | 'cssrsPromptResult'
>;

type TreatmentTabProps = {
  patientShortId: string;
  completedForms?: CombinedFormDataEntriesByFormTitle;
  hasNewForms?: boolean;
  nudges: NudgeObject[];
  patientId: string;
  shouldShowFormsSection?: boolean;
  isInSessionWithSelectedClient: boolean;
  shouldShowClinicalConsiderationSection: boolean;
  onClickTreatmentPlans: (treatmentPlanStatus: TreatmentPlanStatus) => void;
  treatmentPlansComponent: React.FC<TreatmentPlansOverviewConnectedProps>;
  clientInformationProgress: React.FC;
  shouldShowAnytimeCSSRS: boolean;
} & FormsCssrsPromptProps;

export function TreatmentTab({
  completedForms,
  hasNewForms,
  patientId,
  patientShortId,
  shouldShowFormsSection,
  onClickTreatmentPlans,
  isInSessionWithSelectedClient,
  shouldShowClinicalConsiderationSection,
  nudges,
  shouldShowAnytimeCSSRS,
  treatmentPlansComponent: TreatmentPlans,
  clientInformationProgress: ClientInformationProgress,
  cssrsPromptLoading,
  cssrsPromptResult,
}: TreatmentTabProps) {
  return (
    <>
      {shouldShowClinicalConsiderationSection && (
        <ClinicalConsiderationsSection
          nudges={nudges}
          patientId={patientId}
          isInSessionWithSelectedClient={isInSessionWithSelectedClient}
        />
      )}
      <div data-testid='client-info.treatment.progress'>
        <ClientInformationProgress />
      </div>
      {shouldShowFormsSection && (
        <Card className='rebrand:bg-surface-inverseEmphasis'>
          <ClientInformationFormsAndQuestionnaires
            completedForms={completedForms}
            hasNewForms={hasNewForms}
            isInSessionWithSelectedClient={isInSessionWithSelectedClient}
            shouldShowAnytimeCSSRS={shouldShowAnytimeCSSRS}
            cssrsPromptLoading={cssrsPromptLoading}
            cssrsPromptResult={cssrsPromptResult}
          />
        </Card>
      )}
      <div className='pt-4' data-testid='client-info.treatment.treatment-plans'>
        <TreatmentPlans
          patientShortId={patientShortId}
          onClick={() => onClickTreatmentPlans(TreatmentPlanStatus.CURRENT_GOALS)}
          isInverse
        />
      </div>
    </>
  );
}

export function TreatmentTabWrapper() {
  const selectedClientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClientInfo?.patientShortId ?? '';
  const appointmentShortId = selectedClientInfo?.inSessionAppointmentShortId;
  const measureBundles = useAtomValue(selectedClientMeasuresData);

  const { loading: cssrsPromptLoading, result: cssrsPromptResult } = useShouldPromptForCSSRS({
    patientShortId,
    measureBundles,
    appointmentShortId,
  });

  useGetFormsByPatientShortId(patientShortId);
  const completedForms = useAtomValue(completedFormsForSelectedClientAtom);
  const nudges = selectedClientInfo?.nudges ?? [];
  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId));
  const patientId = useAtomValue(selectedPatientId) ?? '';
  const { value: showAnytimeCSSRS } = useFeatureGate(showAnytimeCSSRSGate);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setTreatmentPlanContentStatus = useSetAtom(treatmentPlanContentStatus);
  const onClickTreatmentPlans = (treatmentPlanStatus: TreatmentPlanStatus) => {
    setTreatmentPlanContentStatus(treatmentPlanStatus);
    setDrawerState(ProviderDrawerState.TREATMENT_PLANS_CONTENT);
  };

  const hasCompletedForms = completedForms && Object.values(completedForms).length > 0;
  const shouldShowClinicalConsiderationSection = !!nudges.length;

  const isInSessionWithSelectedClient = useAtomValue(isInSessionWithSelectedClientAtom);
  const shouldShowFormsSection = isInSessionWithSelectedClient || hasCompletedForms;
  const measuresFetchedForPatient = useAtomValue(patientShortIdWithFetchedMeasuresAtom);
  const measuresFetchFailedForPatient = useAtomValue(patientShortIdToMeasuresFetchFailedAtom)[patientShortId];
  const hasCompletedMeasureResponses = useAtomValue(patientHasCompletedMeasureResponsesFamily(patientShortId));
  const hasMeasuresData = !!patientShortId && measuresFetchedForPatient === patientShortId;
  const isMeasuresDataReady = !!patientShortId && (hasMeasuresData || !!measuresFetchFailedForPatient);

  useSendClientInfoTabViewTrackingEvent(
    patientShortId,
    EventTypeEnum.TREATMENT_TAB,
    hasMeasuresData ? { hasCompletedMeasureResponses } : {},
    isMeasuresDataReady,
  );

  return (
    <TreatmentTab
      shouldShowAnytimeCSSRS={showAnytimeCSSRS}
      hasNewForms={hasNewForms}
      nudges={nudges}
      completedForms={completedForms}
      patientShortId={patientShortId}
      onClickTreatmentPlans={onClickTreatmentPlans}
      shouldShowFormsSection={shouldShowFormsSection}
      shouldShowClinicalConsiderationSection={shouldShowClinicalConsiderationSection}
      patientId={patientId}
      isInSessionWithSelectedClient={isInSessionWithSelectedClient}
      treatmentPlansComponent={TreatmentPlansOverviewConnected}
      clientInformationProgress={ClientInformationProgress}
      cssrsPromptLoading={cssrsPromptLoading}
      cssrsPromptResult={cssrsPromptResult}
    />
  );
}
